import React, { useState } from 'react';
import { MasterDocument } from '@util/firestore/master';

interface OverviewKeyFeaturesProps {
  master: MasterDocument;
  typeSuffix?: string;
}

const READ_MORE_THRESHOLD = 1000;

/**
 * Renders inline markdown bold (**text**) as <strong> elements.
 * Returns an array of strings and React elements.
 */
function renderInlineBold(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(<strong key={match.index}>{match[1]}</strong>);
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

const OverviewKeyFeatures = ({ master, typeSuffix = '' }: OverviewKeyFeaturesProps) => {
  const [expanded, setExpanded] = useState(false);
  const [featuresExpanded, setFeaturesExpanded] = useState(false);

  // Resolve overview text: gmOverview takes priority, fall back to description (legacy, read-only)
  const overviewText = master.gmOverview || master.description || '';
  const hasOverview = overviewText.trim().length > 0;
  const hasFeatures = Array.isArray(master.gmFeatures) && master.gmFeatures.length > 0;

  // If nothing to show, render nothing
  if (!hasOverview && !hasFeatures) {
    return null;
  }

  const showReadMoreButton = hasOverview && overviewText.length > READ_MORE_THRESHOLD;

  // Split full text into paragraphs: use existing newlines if present, otherwise
  // split every ~3 sentences using ". [A-Z]" boundary (avoids false splits
  // on decimal numbers like "45.0MP" or "29.97fps" in camera specs)
  const byNewline = overviewText.split(/\n+/).filter(p => p.trim().length > 0);
  let paragraphs: string[];
  if (byNewline.length > 1) {
    paragraphs = byNewline;
  } else {
    const sentences = overviewText.split(/(?<=\.)\s+(?=[A-Z])/);
    if (sentences.length <= 3) {
      paragraphs = [overviewText];
    } else {
      paragraphs = [];
      for (let i = 0; i < sentences.length; i += 3) {
        paragraphs.push(sentences.slice(i, i + 3).join(' ').trim());
      }
    }
  }

  // Determine layout: full-width overview when no features, otherwise two-column grid
  const gridClass = hasOverview && hasFeatures
    ? 'grid grid-cols-1 md:grid-cols-2 gap-[3rem]'
    : 'grid grid-cols-1';

  return (
    <section id="overview" aria-label="Overview">
      <div className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]">
        <div className={`${gridClass} items-start`}>
          {/* Left column — Overview text */}
          {hasOverview && (
            <div>
              <h2 className="text-[2rem] sm:text-[2.4rem] font-extrabold text-black mb-[2rem]">
                {typeSuffix ? `${typeSuffix.trim()} ` : ''}Overview<span className="sr-only"> — {master.brand} {master.model}</span>
              </h2>
              <div
                className="text-[1.5rem] leading-[2.4rem] text-brand-dark-gray"
                style={showReadMoreButton && !expanded ? {
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 13,
                } : undefined}
              >
                {paragraphs.map((para, i) => (
                  <p key={i} className={i < paragraphs.length - 1 ? 'mb-[1rem]' : ''}>
                    {renderInlineBold(para)}
                  </p>
                ))}
              </div>

              {showReadMoreButton && (
                <button
                  className="text-brand-orange font-medium mt-[0.8rem] cursor-pointer hover:underline text-[1.4rem]"
                  onClick={() => setExpanded((prev) => !prev)}
                  aria-expanded={expanded}
                >
                  {expanded ? 'Show Less' : 'Read More'}
                </button>
              )}
            </div>
          )}

          {/* Right column — Key Features */}
          {hasFeatures && (
            <div>
              <h2 className="text-[2rem] sm:text-[2.4rem] font-extrabold text-black mb-[2rem]">
                Key Features<span className="sr-only"> — {master.brand} {master.model}</span>
              </h2>
              <div role="list" aria-label="Key Features">
              {master.gmFeatures!.map((feature, index) => (
                <div
                  key={index}
                  role="listitem"
                  className={`mb-[1.2rem]${
                    !featuresExpanded && index >= 6 ? ' hidden' : ''
                  }`}
                >
                  <p className="text-[1.5rem] leading-[2rem] text-gray-600">
                    <span className="font-bold text-brand-dark-gray">{feature.title}:</span>{' '}
                    {feature.description}
                  </p>
                </div>
              ))}
              </div>
              {master.gmFeatures!.length > 6 && (
                <button
                  type="button"
                  className="text-brand-orange font-medium mt-[0.8rem] cursor-pointer hover:underline text-[1.4rem]"
                  onClick={() => setFeaturesExpanded((prev) => !prev)}
                  aria-expanded={featuresExpanded}
                >
                  {featuresExpanded ? 'Show Less' : 'Read More'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default OverviewKeyFeatures;
